import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Observable, from, of } from 'rxjs';
import { map, switchMap, tap, shareReplay, catchError } from 'rxjs/operators';

import { User } from '../models/user';
import { CallableFunction, Collection } from '../resources/app-firebase';
import { Log } from '../utils/log';
import { FirebaseAuthError } from '../errors/firebase-auth-error';
import { AuthError } from '../errors/auth-error';
import { UserUtil } from '../utils/user-util';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import * as Sentry from '@sentry/angular';
import { ALLOW_MULTI_LOGIN_SCHOOL_IDS } from '../resources/config';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  signedInUserId$: Observable<string>;

  constructor(private afAuth: AngularFireAuth, private afs: AngularFirestore, private aff: AngularFireFunctions) {
    this.signedInUserId$ = this.afAuth.authState.pipe(
      map(firebaseUser => (firebaseUser ? firebaseUser.uid : '')),
      shareReplay(1)
    );
  }

  signIn(email: string, password: string): Observable<User> {
    // 1回目のサインイン（uid取得用 + とりあえずこのクライアントでログイン）
    return from(this.afAuth.signInWithEmailAndPassword(email, password)).pipe(
      switchMap(cred => {
        const uid = cred.user?.uid;
        if (!uid) {
          throw AuthError.userNotFound('指定されたユーザーが見つかりませんでした');
        }

        // ここで一度 User ドキュメントを読む（schoolId を知るため）
        return this.afs
          .collection<User>(Collection.USER)
          .doc(uid)
          .get()
          .pipe(
            map(snapshot => {
              if (!snapshot.exists) {
                throw AuthError.userNotFound('指定されたユーザーが見つかりませんでした');
              }
              const user = snapshot.data() as User;
              if (user.isActive === false) {
                throw AuthError.userDisabled('指定されたユーザは無効です');
              }
              return { uid, user: UserUtil.setDefaultValues(user) };
            })
          );
      }),
      switchMap(({ uid, user }) => {
        const isMultiLoginAllowed = ALLOW_MULTI_LOGIN_SCHOOL_IDS.includes(user.schoolId);

        // schoolId が対象なら revoke & 2回目のsignInを「スキップ」
        if (isMultiLoginAllowed) {
          Sentry.setUser({
            email: user.email,
            id: user.id,
            username: `${user.school} ${user.familyName}${user.firstName}`
          });
          return of(user);
        }

        // ===== 通常ケース：revoke してから2回目の signIn =====
        const callable = this.aff.httpsCallable<{ id: string }, void>(CallableFunction.REFRESH_TOKEN_USER);
        return callable({ id: uid }).pipe(
          switchMap(() =>
            from(this.afAuth.signInWithEmailAndPassword(email, password)).pipe(
              map(() => {
                // 2回目 signIn 後に Sentry のユーザ情報をセット
                Sentry.setUser({
                  email: user.email,
                  id: user.id,
                  username: `${user.school} ${user.familyName}${user.firstName}`
                });
                // User情報は最初に読んだものをそのまま返す
                return user;
              })
            )
          )
        );
      }),
      // ログイン時に割当プランから visibleSubjectIds を再計算・同期する（現在セッションへ反映）
      switchMap((user: User) => this.syncVisibleSubjectIds(user))
    );
  }

  /**
   * サーバー側で現在アクティブなプランから visibleSubjectIds を再計算・user 更新し、
   * 更新後の User を返す。失敗してもログインはブロックせず元の user を返す。
   */
  private syncVisibleSubjectIds(user: User): Observable<User> {
    const callable = this.aff.httpsCallable<null, User>(CallableFunction.SYNC_VISIBLE_SUBJECT_IDS);
    return callable(null).pipe(
      map(updatedUser => UserUtil.setDefaultValues(updatedUser || user)),
      catchError(e => {
        Log.error(this.constructor.name, 'visibleSubjectIds の同期に失敗しました', e);
        return of(user);
      })
    );
  }

  signOut(): Observable<void> {
    const promise = this.afAuth.signOut();
    return from(promise).pipe(tap(() => Log.debug(this.constructor.name, 'sign out exec')));
  }

  sendPasswordResetEmail(email: string): Observable<void> {
    const promise = this.afAuth.sendPasswordResetEmail(email).catch(e => {
      throw FirebaseAuthError.from(e);
    });
    return from(promise);
  }
}
